import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

interface TokenExchangeResponse {
  accessToken: string;
  expiresAt: string;
}

interface InMemoryToken extends TokenExchangeResponse {
  expiresAtMs: number;
}

interface TassoQuintoRequestConfig extends InternalAxiosRequestConfig {
  tassoQuintoRetried?: boolean;
  tassoQuintoAccessToken?: string;
}

export interface TassoQuintoSessionOptions {
  apiClient: AxiosInstance;
  exchangeClient: AxiosInstance;
  exchangePath: string;
  getImpersonatedUserId: () => string | null;
  onExchangeUnauthorized: () => void | Promise<void>;
  now?: () => number;
  expirySkewMs?: number;
}

export interface TassoQuintoSession {
  apiClient: AxiosInstance;
  clear: () => void;
}

const DEFAULT_EXPIRY_SKEW_MS = 30_000;

export const createTassoQuintoSession = ({
  apiClient,
  exchangeClient,
  exchangePath,
  getImpersonatedUserId,
  onExchangeUnauthorized,
  now = Date.now,
  expirySkewMs = DEFAULT_EXPIRY_SKEW_MS,
}: TassoQuintoSessionOptions): TassoQuintoSession => {
  let token: InMemoryToken | null = null;
  let exchangeInFlight: Promise<string> | null = null;
  let sessionGeneration = 0;

  const hasUsableToken = () =>
    token !== null && token.expiresAtMs - expirySkewMs > now();

  const exchangeToken = async (exchangeGeneration: number): Promise<string> => {
    const impersonatedUserId = getImpersonatedUserId();
    const body = impersonatedUserId ? { impersonatedUserId } : {};

    try {
      const exchangeResponse = await exchangeClient.post<
        TokenExchangeResponse,
        AxiosResponse<TokenExchangeResponse>,
        { impersonatedUserId?: string }
      >(exchangePath, body);
      const expiresAtMs = Date.parse(exchangeResponse.data.expiresAt);

      if (exchangeGeneration !== sessionGeneration) {
        throw new Error('Tasso Quinto session changed during token exchange');
      }

      if (!exchangeResponse.data.accessToken || Number.isNaN(expiresAtMs)) {
        throw new Error('Invalid Tasso Quinto token exchange response');
      }

      token = { ...exchangeResponse.data, expiresAtMs };
      return token.accessToken;
    } catch (error) {
      if (exchangeGeneration !== sessionGeneration) {
        throw new Error('Tasso Quinto session changed during token exchange');
      }

      if (axios.isAxiosError(error) && error.response?.status === 401) {
        token = null;
        await onExchangeUnauthorized();
      }
      throw error;
    }
  };

  const getAccessToken = (): Promise<string> => {
    if (hasUsableToken()) {
      return Promise.resolve(token!.accessToken);
    }

    if (!exchangeInFlight) {
      const currentExchange = exchangeToken(sessionGeneration).finally(() => {
        if (exchangeInFlight === currentExchange) {
          exchangeInFlight = null;
        }
      });
      exchangeInFlight = currentExchange;
    }

    return exchangeInFlight;
  };

  const refreshAfterUnauthorized = (rejectedToken?: string) => {
    if (
      rejectedToken &&
      token?.accessToken !== rejectedToken &&
      hasUsableToken()
    ) {
      return Promise.resolve(token!.accessToken);
    }

    token = null;
    return getAccessToken();
  };

  apiClient.interceptors.request.use(async (config) => {
    const request = config as TassoQuintoRequestConfig;
    const accessToken = await getAccessToken();

    request.headers.set('Authorization', `Bearer ${accessToken}`);
    request.tassoQuintoAccessToken = accessToken;
    return request;
  });

  apiClient.interceptors.response.use(undefined, async (error) => {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) {
      throw error;
    }

    const request = error.config as TassoQuintoRequestConfig | undefined;
    if (!request?.tassoQuintoAccessToken || request.tassoQuintoRetried) {
      throw error;
    }

    request.tassoQuintoRetried = true;
    await refreshAfterUnauthorized(request.tassoQuintoAccessToken);
    return apiClient.request(request);
  });

  return {
    apiClient,
    clear: () => {
      sessionGeneration += 1;
      token = null;
      exchangeInFlight = null;
    },
  };
};
